import { useEffect, useRef, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonFooter, IonIcon,
    IonInput, IonTextarea, IonCheckbox, IonToggle, IonSelect, IonSelectOption,
    IonRadioGroup, IonRadio, IonText, IonAlert, IonSpinner,
} from '@ionic/react';
import { closeOutline, pencilOutline } from 'ionicons/icons';
import { SettingsFieldConfig, SettingsModalProps } from '@/types/model/settingsModal';
import TransactionReasonModal from './TransactionReasonModal';
import Toaster, { ToasterVariant } from './Toaster';
import ActionIconButton from './ActionIconButton';

type Mode = 'view' | 'edit';

function SettingsModal<T extends object>({
    isOpen, onDidDismiss, title, fields, initialData, emptyData, onSave, onSaved,
    validateForm, requireReason, reasonTitle, canEdit = true, width = 'min(80vw, 1400px)', height = '90vh',
    successMessage = 'Changes saved.', errorMessage = 'Failed to save changes.', secondaryAction,
}: SettingsModalProps<T>) {
    const [mode, setMode] = useState<Mode>('view');
    const [formData, setFormData] = useState<T>(emptyData);
    const [originalFormData, setOriginalFormData] = useState<T>(emptyData);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [showExitAlert, setShowExitAlert] = useState(false);
    const [showCancelAlert, setShowCancelAlert] = useState(false);
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [showSecondaryActionAlert, setShowSecondaryActionAlert] = useState(false);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);
    const forceClosingRef = useRef(false);

    useEffect(() => {
        if (isOpen) {
            forceClosingRef.current = false;
            const initial = initialData ?? emptyData;
            setFormData(initial);
            setOriginalFormData(initial);
            setMode('view');
            setErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialData]);

    const hasUnsavedChanges = () => JSON.stringify(formData) !== JSON.stringify(originalFormData);

    const update = (key: keyof T, value: unknown) => {
        setFormData(prev => ({ ...prev, [key]: value }));
        setErrors(prev => ({ ...prev, [key as string]: '' }));
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        fields.forEach(field => {
            if (field.visible && !field.visible(formData)) return;
            const message = field.validate?.(formData[field.key], formData);
            if (message) newErrors[field.key as string] = message;
        });
        if (validateForm) Object.assign(newErrors, validateForm(formData));
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClose = () => {
        if (forceClosingRef.current) return;
        if (mode === 'edit' && hasUnsavedChanges()) setShowExitAlert(true);
        else onDidDismiss();
    };

    const handleForceClose = () => {
        setShowExitAlert(false);
        forceClosingRef.current = true;
        onDidDismiss();
    };

    const handleCancel = () => {
        if (hasUnsavedChanges()) setShowCancelAlert(true);
        else { setFormData(originalFormData); setErrors({}); setMode('view'); }
    };

    const handleDiscardToView = () => {
        setShowCancelAlert(false);
        setFormData(originalFormData);
        setErrors({});
        setMode('view');
    };

    const persist = async (reason?: string) => {
        setIsSaving(true);
        try {
            await onSave(formData, reason);
            setOriginalFormData(formData);
            setMode('view');
            setToast({ message: successMessage, variant: 'success' });
            onSaved?.();
        } catch {
            setToast({ message: errorMessage, variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveClick = () => {
        if (!validate()) return;
        if (requireReason) setShowReasonModal(true);
        else void persist();
    };

    const handleReasonConfirm = (reason: string) => {
        setShowReasonModal(false);
        void persist(reason);
    };

    const handleSecondaryActionConfirm = async () => {
        if (!secondaryAction) return;

        setShowSecondaryActionAlert(false);
        setIsSaving(true);
        try {
            await secondaryAction.onExecute();
            onSaved?.();
            setToast({ message: secondaryAction.successMessage, variant: 'success' });
            if (secondaryAction.dismissOnSuccess) {
                forceClosingRef.current = true;
                onDidDismiss();
            }
        } catch {
            setToast({ message: secondaryAction.errorMessage, variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const visibleFields = fields.filter(f => !f.visible || f.visible(formData));

    const renderViewValue = (field: SettingsFieldConfig<T>): string => {
        const value = formData[field.key];
        if (field.formatView) return field.formatView(value, formData);
        if (field.type === 'select' || field.type === 'multiselect') {
            const values = Array.isArray(value) ? (value as string[]) : [String(value ?? '')];
            return values.map(v => field.options?.find(o => o.value === v)?.label ?? v).join(', ') || '—';
        }
        return value != null && value !== '' ? String(value) : '—';
    };

    const renderControl = (field: SettingsFieldConfig<T>, isDisabled: boolean) => {
        const value = formData[field.key];
        const disabledStyle = isDisabled ? { opacity: 0.6, border: 'none' } : {};
        
        switch (field.type) {
            case 'readonly':
                return <IonText style={{ fontSize: '0.95rem' }}>{renderViewValue(field)}</IonText>;
            case 'textarea':
                return (
                    <IonTextarea
                        disabled={isDisabled}
                        value={String(value ?? '')}
                        placeholder={field.placeholder}
                        onIonInput={e => update(field.key, e.detail.value ?? '')}
                        rows={4}
                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px', '--padding-start': '8px', ...disabledStyle }}
                    />
                );
            case 'checkbox':
                return (
                    <IonCheckbox disabled={isDisabled} checked={Boolean(value)} onIonChange={e => update(field.key, e.detail.checked)} style={disabledStyle} />
                );
            case 'toggle':
                return <IonToggle disabled={isDisabled} checked={Boolean(value)} onIonChange={e => update(field.key, e.detail.checked)} style={disabledStyle} />;
            case 'select':
                return (
                    <IonSelect disabled={isDisabled} value={String(value ?? '')} onIonChange={e => update(field.key, e.detail.value)}
                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px', '--padding-start': '8px', ...disabledStyle }}>
                        {field.options?.map(o => <IonSelectOption key={o.value} value={o.value}>{o.label}</IonSelectOption>)}
                    </IonSelect>
                );
            case 'multiselect':
                return (
                    <IonSelect disabled={isDisabled} multiple value={Array.isArray(value) ? value : []} onIonChange={e => update(field.key, e.detail.value)}
                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px', '--padding-start': '8px', ...disabledStyle }}>
                        {field.options?.map(o => <IonSelectOption key={o.value} value={o.value}>{o.label}</IonSelectOption>)}
                    </IonSelect>
                );
            case 'radio':
                return (
                    <div style={isDisabled ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
                        <IonRadioGroup value={String(value ?? '')} onIonChange={e => update(field.key, e.detail.value)}>
                            <div style={{ display: 'flex', gap: '1.5rem', padding: '0.25rem 0' }}>
                                {field.options?.map(o => <IonRadio key={o.value} value={o.value}>{o.label}</IonRadio>)}
                            </div>
                        </IonRadioGroup>
                    </div>
                );
            case 'number':
                return (
                    <IonInput
                        disabled={isDisabled}
                        type="number"
                        value={String(value ?? '')}
                        placeholder={field.placeholder}
                        onIonInput={e => update(field.key, e.detail.value ?? '')}
                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px', '--padding-start': '8px', ...disabledStyle }}
                    />
                );
            case 'text':
            default:
                return (
                    <IonInput
                        disabled={isDisabled}
                        value={String(value ?? '')}
                        placeholder={field.placeholder}
                        onIonInput={e => update(field.key, e.detail.value ?? '')}
                        style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px', '--padding-start': '8px', ...disabledStyle }}
                    />
                );
        }
    };

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={handleClose} style={{ '--width': width, '--height': height, '--max-width': '1400px', '--border-radius': '8px' }}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle style={{ fontSize: '1rem' }}>{title}</IonTitle>
                        <IonButtons slot="end">
                            {mode === 'view' && canEdit && (
                                <IonButton onClick={() => setMode('edit')} title="Edit" aria-label="Edit">
                                    <IonIcon icon={pencilOutline} />
                                </IonButton>
                            )}
                            <IonButton onClick={handleClose} disabled={isSaving} aria-label="Close">
                                <IonIcon icon={closeOutline} />
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                {mode === 'edit' && secondaryAction && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.5rem 1rem', borderBottom: '1px solid var(--ion-color-light-shade)' }}>
                        <ActionIconButton
                            icon={secondaryAction.icon}
                            label={secondaryAction.label}
                            disabled={isSaving}
                            onClick={() => setShowSecondaryActionAlert(true)}
                            buttonStyle={{ margin: 0 }}
                        />
                    </div>
                )}

                <IonContent style={{ '--padding-start': '0', '--padding-end': '0', '--padding-top': '0', '--padding-bottom': '0' }}>
                    {isSaving ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                            <IonSpinner />
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0, margin: '1rem' }}>
                            {visibleFields.map((field, index) => {
                                const isTextarea = field.type === 'textarea';
                                const isViewMode = mode === 'view';
                                const previousSection = index > 0 ? visibleFields[index - 1].section : undefined;
                                const startsSection = field.section !== previousSection;
                                const errorElement = errors[field.key as string] && (
                                    <IonText color="danger" style={{ fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>
                                        {errors[field.key as string]}
                                    </IonText>
                                );
                                const labelStyle = { fontWeight: '200', fontSize: '0.85rem', color: 'var(--ion-text-color)' };

                                return (
                                    <div key={field.key as string} style={{ display: 'contents' }}>
                                        {startsSection && field.section && (
                                            <div style={{ gridColumn: '1', display: 'flex', alignItems: 'center', gap: '0.75rem', margin: index === 0 ? '0.5rem 0' : '1.5rem 0 0.5rem' }}>
                                                {/* {index > 0 && <div style={{ height: '1px', flex: 1, background: 'var(--ion-color-light-shade)' }} />} */}
                                                <IonText style={{ fontSize: '0.9rem', fontWeight: '700', color: 'var(--ion-color-dark)' }}>{field.section}</IonText>
                                                <div style={{ height: '1px', flex: 1, background: 'var(--ion-color-light-shade)' }} />
                                            </div>
                                        )}
                                        {isTextarea ? (
                                            // Textarea: spans full width, label above input
                                            <div style={{ gridColumn: '1', padding: '1rem' }}>
                                                <div style={{ ...labelStyle, display: 'block', marginBottom: '0.5rem' }}>
                                                    {field.label}
                                                </div>
                                                {field.helperText && <IonText color="medium" style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.5rem' }}>{field.helperText}</IonText>}
                                                {renderControl(field, isViewMode)}
                                                {errorElement}
                                            </div>
                                        ) : (
                                            // Other fields: two-column layout (label, input)
                                            <div style={{ gridColumn: '1', display: 'grid', gridTemplateColumns: '60% 40%', alignItems: 'start', gap: '1rem', padding: '1rem'}}>
                                                <div style={{ ...labelStyle, paddingTop: '0.25rem' }}>
                                                    {field.label}
                                                    {field.helperText && <IonText color="medium" style={{ display: 'block', fontSize: '0.8rem', fontWeight: '400', marginTop: '0.25rem' }}>{field.helperText}</IonText>}
                                                </div>
                                                <div>
                                                    {renderControl(field, isViewMode)}
                                                    {errorElement}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </IonContent>

                {mode === 'edit' && !isSaving && (
                    <IonFooter>
                        <IonToolbar style={{ '--padding-start': '1rem', '--padding-end': '1rem' }}>
                            <IonButtons slot="start">
                                <IonButton color="medium" onClick={handleCancel}>Cancel</IonButton>
                            </IonButtons>
                            <IonButtons slot="end">
                                <IonButton color="primary" fill="solid" disabled={!hasUnsavedChanges()} onClick={handleSaveClick}>
                                    Save
                                </IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonFooter>
                )}
            </IonModal>

            <IonAlert isOpen={showCancelAlert} header="Discard Changes?" message="Your changes will not be saved."
                buttons={[
                    { text: 'Keep Editing', role: 'cancel', handler: () => setShowCancelAlert(false) },
                    { text: 'Discard', role: 'destructive', handler: handleDiscardToView },
                ]}
                onDidDismiss={() => setShowCancelAlert(false)}
            />
            <IonAlert isOpen={showExitAlert} header="Unsaved Changes" message="You have unsaved changes. Close anyway?"
                buttons={[
                    { text: 'Keep Editing', role: 'cancel', handler: () => setShowExitAlert(false) },
                    { text: 'Close', role: 'destructive', handler: handleForceClose },
                ]}
                onDidDismiss={() => setShowExitAlert(false)}
            />
            <IonAlert isOpen={showSecondaryActionAlert} header={secondaryAction?.confirmHeader} message={secondaryAction?.confirmMessage}
                buttons={[
                    { text: 'Cancel', role: 'cancel', handler: () => setShowSecondaryActionAlert(false) },
                    { text: secondaryAction?.confirmLabel ?? secondaryAction?.label ?? 'Confirm', role: 'destructive', handler: () => { void handleSecondaryActionConfirm(); } },
                ]}
                onDidDismiss={() => setShowSecondaryActionAlert(false)}
            />
            <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
            {requireReason && (
                <TransactionReasonModal isOpen={showReasonModal} title={reasonTitle ?? title}
                    onConfirm={handleReasonConfirm} onCancel={() => setShowReasonModal(false)} />
            )}
        </>
    );
}

export default SettingsModal;
